import { useEffect, useRef, useState, type RefObject } from 'react';
import type { OverlayConfig, CropConfig } from '../types';
import { PIP_CORNERS, PIP_DEFAULT_X, PIP_DEFAULT_Y, PIP_DEFAULT_W, type PipCorner } from '../utils';
import { Button, Group, Text } from '@mantine/core';
import Check from 'lucide-react/dist/esm/icons/check';
import CropIcon from 'lucide-react/dist/esm/icons/crop';
import ArrowUpLeft from 'lucide-react/dist/esm/icons/arrow-up-left';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import ArrowDownLeft from 'lucide-react/dist/esm/icons/arrow-down-left';
import ArrowDownRight from 'lucide-react/dist/esm/icons/arrow-down-right';
import { ActionIcon, Tooltip } from '@mantine/core';
import classes from '../editor.module.css';

interface Props {
  videoRef: RefObject<HTMLVideoElement>; pipVideoRef?: RefObject<HTMLVideoElement>; isLoading: boolean;
  canvasWidth?: number; canvasHeight?: number; camWidth?: number; camHeight?: number;
  activePip: { segId: string; segmentNumber: number; overlay: OverlayConfig } | null;
  onPipFinish: (x: number, y: number, w: number) => void;
  onPipSnap?: (corner: PipCorner) => void;
  activeCrop?: { crop: CropConfig; sourceWidth: number; sourceHeight: number } | null;
  isCropping?: boolean; onCloseCrop?: () => void; onCropChange?: (crop: CropConfig) => void;
}

type CropHandle = 'top' | 'bottom' | 'left' | 'right' | 'corner_br' | 'corner_tl';

export function VideoPlayer({
  videoRef, pipVideoRef, isLoading, activePip, onPipFinish,
  canvasWidth = 1920, canvasHeight = 1080, camWidth = 1920, camHeight = 1080,
  activeCrop, isCropping, onCloseCrop, onCropChange, onPipSnap,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [localOverlay, setLocalOverlay] = useState<OverlayConfig | null>(null);
  const drag = useRef({ active: false, type: 'move' as 'move' | 'resize', startX: 0, startY: 0, startLeft: 0, startTop: 0, startWidth: 0 });
  const cropDrag = useRef<{ active: boolean; handle: CropHandle; startX: number; startY: number; startCrop: CropConfig }>({ active: false, handle: 'bottom', startX: 0, startY: 0, startCrop: { top: 0, bottom: 0, left: 0, right: 0 } });

  useEffect(() => { if (!drag.current.active) setLocalOverlay(activePip?.overlay ?? null); }, [activePip]);

  const overlay = localOverlay ?? activePip?.overlay;
  const oc = overlay?.crop;
  const effCamW = Math.max(16, camWidth - (oc?.left || 0) - (oc?.right || 0));
  const effCamH = Math.max(16, camHeight - (oc?.top || 0) - (oc?.bottom || 0));
  const effCamWRef = useRef(effCamW); effCamWRef.current = effCamW;

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();

      if (cropDrag.current.active && activeCrop && onCropChange) {
        const cd = cropDrag.current, srcW = activeCrop.sourceWidth || 1920, srcH = activeCrop.sourceHeight || 1080;
        const dx = Math.round(((e.clientX - cd.startX) / rect.width) * srcW);
        const dy = Math.round(((e.clientY - cd.startY) / rect.height) * srcH);
        const clampDim = (val: number, maxD: number) => Math.max(0, Math.min(Math.floor(maxD * 0.48), val & ~1));
        const h = cd.handle;
        onCropChange({
          top: ['top', 'corner_tl'].includes(h) ? clampDim(cd.startCrop.top + dy, srcH) : (cd.startCrop.top || 0),
          bottom: ['bottom', 'corner_br'].includes(h) ? clampDim(cd.startCrop.bottom - dy, srcH) : (cd.startCrop.bottom || 0),
          left: ['left', 'corner_tl'].includes(h) ? clampDim(cd.startCrop.left + dx, srcW) : (cd.startCrop.left || 0),
          right: ['right', 'corner_br'].includes(h) ? clampDim(cd.startCrop.right - dx, srcW) : (cd.startCrop.right || 0),
        });
        return;
      }

      const d = drag.current;
      if (!d.active) return;
      const dx = (e.clientX - d.startX) / rect.width;
      if (d.type === 'move') {
        const dy = (e.clientY - d.startY) / rect.height;
        setLocalOverlay(prev => prev ? { ...prev, x: d.startLeft + dx, y: d.startTop + dy } : null);
      } else {
        setLocalOverlay(prev => prev ? { ...prev, w: Math.max(0.05, Math.min(1.5, d.startWidth + dx * (camWidth / effCamWRef.current))) } : null);
      }
    }

    function onMouseUp() {
      cropDrag.current.active = false;
      if (drag.current.active) {
        drag.current.active = false;
        setLocalOverlay(latest => { if (latest) onPipFinish(latest.x, latest.y, latest.w); return latest; });
      }
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
  }, [onPipFinish, activeCrop, onCropChange, camWidth]);

  const isVisible = Boolean(activePip && overlay);
  const isColor = Boolean(overlay?.color_enabled);
  const gain = isColor ? Math.max(0, overlay?.gain ?? 1) : 1, lift = isColor ? (overlay?.lift ?? 0) : 0;
  const svgExp = (1 / (isColor ? Math.max(0.1, overlay?.gamma ?? 1) : 1)).toFixed(4);

  const srcW = activeCrop?.sourceWidth || canvasWidth, srcH = activeCrop?.sourceHeight || canvasHeight;
  const cropT = activeCrop?.crop.top || 0, cropB = activeCrop?.crop.bottom || 0, cropL = activeCrop?.crop.left || 0, cropR = activeCrop?.crop.right || 0;
  const hasCrop = (cropT > 0 || cropB > 0 || cropL > 0 || cropR > 0);
  const scale = Math.min(canvasWidth / Math.max(16, srcW - cropL - cropR), canvasHeight / Math.max(16, srcH - cropT - cropB));

  const scaleX = (overlay?.w ?? PIP_DEFAULT_W) / camWidth, scaleY = scaleX * (canvasWidth / canvasHeight);
  const boxLeft = ((overlay?.x ?? PIP_DEFAULT_X) + (oc?.left || 0) * scaleX) * 100;
  const boxTop = ((overlay?.y ?? PIP_DEFAULT_Y) + (oc?.top || 0) * scaleY) * 100;

  const startCropDrag = (handle: CropHandle, e: React.MouseEvent) => {
    cropDrag.current = { active: true, handle, startX: e.clientX, startY: e.clientY, startCrop: { ...(activeCrop?.crop || { top: 0, bottom: 0, left: 0, right: 0 }) } };
    e.preventDefault(); e.stopPropagation();
  };

  return (
    <div className={classes.videoContainer} ref={wrapperRef} style={{ aspectRatio: `${canvasWidth} / ${canvasHeight}` }}>
      {isLoading && <div className={classes.loadingOverlay}>Загрузка...</div>}
      <svg style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }} aria-hidden="true">
        <filter id="pip-colorgrade" colorInterpolationFilters="sRGB">
          <feComponentTransfer><feFuncR type="gamma" amplitude={gain} exponent={svgExp} offset={lift} /><feFuncG type="gamma" amplitude={gain} exponent={svgExp} offset={lift} /><feFuncB type="gamma" amplitude={gain} exponent={svgExp} offset={lift} /></feComponentTransfer>
        </filter>
      </svg>

      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <video
          ref={videoRef} playsInline
          style={(!isCropping && hasCrop) ? {
            position: 'absolute', left: `${((canvasWidth - (srcW - cropL - cropR) * scale) / 2 - cropL * scale) / canvasWidth * 100}%`,
            top: `${((canvasHeight - (srcH - cropT - cropB) * scale) / 2 - cropT * scale) / canvasHeight * 100}%`,
            width: `${(srcW * scale / canvasWidth) * 100}%`, height: `${(srcH * scale / canvasHeight) * 100}%`, objectFit: 'fill', pointerEvents: 'none',
          } : { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
        />
      </div>

      {isCropping && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 60, pointerEvents: 'none' }}>
          {[
            { top: 0, left: 0, right: 0, height: `${(cropT / srcH) * 100}%` }, { bottom: 0, left: 0, right: 0, height: `${(cropB / srcH) * 100}%` },
            { top: `${(cropT / srcH) * 100}%`, bottom: `${(cropB / srcH) * 100}%`, left: 0, width: `${(cropL / srcW) * 100}%` },
            { top: `${(cropT / srcH) * 100}%`, bottom: `${(cropB / srcH) * 100}%`, right: 0, width: `${(cropR / srcW) * 100}%` },
          ].map((s, i) => <div key={i} style={{ position: 'absolute', background: 'rgba(0,0,0,0.65)', ...s }} />)}

          <div style={{ position: 'absolute', top: `${(cropT / srcH) * 100}%`, bottom: `${(cropB / srcH) * 100}%`, left: `${(cropL / srcW) * 100}%`, right: `${(cropR / srcW) * 100}%`, border: '2px solid var(--mantine-color-brand-4)', pointerEvents: 'auto' }}>
            {[
              { h: 'bottom' as const, s: { bottom: -8, left: '25%', right: '25%', height: 16, cursor: 'ns-resize' }, b: { width: 44, height: 6 } },
              { h: 'top' as const, s: { top: -8, left: '25%', right: '25%', height: 16, cursor: 'ns-resize' }, b: { width: 44, height: 6 } },
              { h: 'left' as const, s: { left: -8, top: '25%', bottom: '25%', width: 16, cursor: 'ew-resize' }, b: { width: 6, height: 44 } },
              { h: 'right' as const, s: { right: -8, top: '25%', bottom: '25%', width: 16, cursor: 'ew-resize' }, b: { width: 6, height: 44 } },
            ].map(({ h, s, b }) => (
              <div key={h} style={{ ...s, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => startCropDrag(h, e)}>
                <div style={{ ...b, background: '#fff', borderRadius: 3, border: '1px solid var(--mantine-color-brand-6)' }} />
              </div>
            ))}
            <div style={{ position: 'absolute', bottom: -6, right: -6, width: 14, height: 14, background: '#fff', border: '2px solid var(--mantine-color-brand-6)', cursor: 'nwse-resize' }} onMouseDown={e => startCropDrag('corner_br', e)} />
            <div style={{ position: 'absolute', top: -6, left: -6, width: 14, height: 14, background: '#fff', border: '2px solid var(--mantine-color-brand-6)', cursor: 'nwse-resize' }} onMouseDown={e => startCropDrag('corner_tl', e)} />
          </div>

          <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'auto' }}>
            <Group gap="xs" px="md" py={6} style={{ background: 'rgba(0,0,0,0.85)', borderRadius: 20, border: '1px solid var(--mantine-color-default-border)' }}>
              <CropIcon size={16} color="var(--mantine-color-brand-4)" /><Text fz="xs" fw={500} c="white">Потяните за края для срезки</Text>
              <Button size="compact-xs" variant="filled" color="blue" leftSection={<Check size={14} />} onClick={() => onCloseCrop?.()}>Готово</Button>
            </Group>
          </div>
        </div>
      )}

      <div
        className={classes.pipBox}
        style={{
          visibility: isVisible ? 'visible' : 'hidden', opacity: isVisible ? 1 : 0, pointerEvents: isVisible ? 'auto' : 'none',
          left: `${boxLeft}%`, top: `${boxTop}%`, width: `${(effCamW * scaleX) * 100}%`, height: `${(effCamH * scaleY) * 100}%`,
        }}
        onMouseDown={e => {
          if (!isVisible || (e.target as HTMLElement).dataset.pipHandle === 'resize') return;
          drag.current = { active: true, type: 'move', startX: e.clientX, startY: e.clientY, startLeft: overlay?.x ?? PIP_DEFAULT_X, startTop: overlay?.y ?? PIP_DEFAULT_Y, startWidth: overlay?.w ?? PIP_DEFAULT_W };
          e.preventDefault();
        }}
      >
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit', pointerEvents: 'none' }}>
          <video
            ref={pipVideoRef} playsInline muted={!activePip?.overlay?.mix_audio}
            style={{
              position: 'absolute', left: `${-((oc?.left || 0) / effCamW) * 100}%`, top: `${-((oc?.top || 0) / effCamH) * 100}%`,
              width: `${(camWidth / effCamW) * 100}%`, height: `${(camHeight / effCamH) * 100}%`,
              objectFit: 'fill', pointerEvents: 'none', display: 'block', zIndex: 1, filter: isColor ? 'url(#pip-colorgrade)' : 'none',
            }}
          />
        </div>
        <div className={classes.pipLabel} style={{ zIndex: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Камера {activePip ? `(Сегм ${activePip.segmentNumber})` : ''}</span>
          {onPipSnap && (
            <Group gap={2} wrap="nowrap" ml={2}>
              {PIP_CORNERS.map(({ corner, label }) => {
                const Icon = corner === 'top-left' ? ArrowUpLeft : corner === 'top-right' ? ArrowUpRight : corner === 'bottom-left' ? ArrowDownLeft : ArrowDownRight;
                return (
                  <Tooltip key={corner} label={label} withArrow openDelay={200}>
                    <ActionIcon size={16} variant="subtle" color="gray" onClick={e => { e.stopPropagation(); onPipSnap(corner); }} aria-label={label}>
                      <Icon size={11} />
                    </ActionIcon>
                  </Tooltip>
                );
              })}
            </Group>
          )}
        </div>
        <div className={classes.pipHandle} data-pip-handle="resize" style={{ zIndex: 10 }} onMouseDown={e => {
          if (!isVisible) return;
          drag.current = { active: true, type: 'resize', startX: e.clientX, startY: e.clientY, startLeft: overlay?.x ?? PIP_DEFAULT_X, startTop: overlay?.y ?? PIP_DEFAULT_Y, startWidth: overlay?.w ?? PIP_DEFAULT_W };
          e.stopPropagation(); e.preventDefault();
        }} />
      </div>
    </div>
  );
}
